/**
 * F-FIRST-RUN-LIVE-DATA Phase 4 — "Bring your own seed-zip" wizard step.
 *
 * Optional step between Firm Setup and Provisioning. Two paths:
 *
 *   1. Skip — provisioning runs with template seed only.
 *      (Identical to pre-Phase-4 behavior.)
 *   2. Upload zip — file gets PUT to Convex storage,
 *      `attachByoZip` stamps the draft, then Continue advances to
 *      provisioning which unpacks + merges the zip server-side.
 *
 * Per Q3-Q5 defaults: only .md files are imported; one matter per
 * top-level folder; files land in `/01-Matters/<CODE>/Inputs/`.
 *
 * UI: file input + drag-drop. No matter-preview UI (Q6 fire-and-
 * forget). Removing the uploaded file calls `clearByoZip`.
 */

import { useState, useCallback, useRef } from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';

export interface BYOSeedStepProps {
  draftId: string | null;
  byoFileName: string | null;
  byoFileSize: number | null;
  onSkip: () => void;
  onContinue: () => void;
  busy?: boolean;
}

const MAX_ZIP_BYTES = 10 * 1024 * 1024; // 10 MB cap — keeps Convex storage usage trivial.

export function BYOSeedStep({
  draftId,
  byoFileName,
  byoFileSize,
  onSkip,
  onContinue,
  busy,
}: BYOSeedStepProps) {
  const generateUploadUrl = useMutation(api.onboardingDrafts.generateByoUploadUrl);
  const attachZip = useMutation(api.onboardingDrafts.attachByoZip);
  const clearZip = useMutation(api.onboardingDrafts.clearByoZip);

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFile = useCallback(
    async (file: File) => {
      if (!draftId) {
        setUploadError('Draft not initialized yet; please wait a moment and try again.');
        return;
      }
      if (!file.name.toLowerCase().endsWith('.zip')) {
        setUploadError(`Only .zip files are supported. Got: ${file.name}`);
        return;
      }
      if (file.size > MAX_ZIP_BYTES) {
        setUploadError(
          `File too large: ${(file.size / 1024 / 1024).toFixed(1)} MB. ` +
            `Cap is ${MAX_ZIP_BYTES / 1024 / 1024} MB.`,
        );
        return;
      }
      setUploadError(null);
      setUploading(true);
      try {
        const url = await generateUploadUrl({});
        const putRes = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/zip' },
          body: file,
        });
        if (!putRes.ok) {
          throw new Error(`Storage upload returned ${putRes.status}`);
        }
        const { storageId } = (await putRes.json()) as { storageId: Id<'_storage'> };
        await attachZip({
          draftId,
          storageId,
          fileName: file.name,
          fileSize: file.size,
        });
      } catch (err) {
        setUploadError(err instanceof Error ? err.message : String(err));
      } finally {
        setUploading(false);
      }
    },
    [draftId, generateUploadUrl, attachZip],
  );

  const handleClear = useCallback(async () => {
    if (!draftId) return;
    setUploadError(null);
    try {
      await clearZip({ draftId });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : String(err));
    }
  }, [draftId, clearZip]);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) void handleFile(file);
    },
    [handleFile],
  );

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) void handleFile(file);
      // Reset so the same file can be re-selected after a Clear.
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [handleFile],
  );

  const hasUpload = byoFileName !== null && byoFileSize !== null;

  return (
    <section
      data-testid="onboard-byo-step"
      className="flex flex-col gap-4 p-4"
    >
      <h2 className="text-lg font-bold text-black">
        Bring your own sample content <span className="text-xs font-normal text-gray-800">(optional)</span>
      </h2>
      <p className="text-sm text-black">
        Upload a .zip of <code>.md</code> and/or <code>.docx</code> files
        to seed your portal with content you already have. We'll create
        one matter per top-level folder and drop the files into{' '}
        <code>/01-Matters/&lt;NAME&gt;/Inputs/</code>. Other file types are
        silently skipped.
      </p>
      <p className="text-sm text-black">
        If you skip this step, your portal lands with the template's
        sample matters only — you can import content later from the
        Obsidian Import window inside the portal.
      </p>

      {hasUpload ? (
        <div
          data-testid="onboard-byo-uploaded"
          className="flex items-center justify-between border-2 border-solid border-[#404040] bg-[#e0ffe0] p-3"
        >
          <div className="flex flex-col">
            <span className="text-sm font-bold text-black">
              ✓ Uploaded: {byoFileName}
            </span>
            <span className="text-xs text-gray-800">
              {(byoFileSize / 1024).toFixed(1)} KB — will be unpacked + merged when provisioning runs
            </span>
          </div>
          <button
            type="button"
            data-testid="onboard-byo-clear"
            onClick={() => void handleClear()}
            disabled={uploading || busy}
            className="border border-[#404040] bg-[#d0d0d0] px-3 py-0.5 text-xs text-black cursor-pointer hover:bg-[#e0e0e0] disabled:opacity-50"
          >
            Remove
          </button>
        </div>
      ) : (
        <div
          data-testid="onboard-byo-dropzone"
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed p-8 ${
            dragOver ? 'border-[#316ac5] bg-[#e0e8f8]' : 'border-[#808080] bg-[#f0f0f0]'
          }`}
        >
          <p className="text-sm text-black">
            Drag a .zip here, or
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".zip"
            onChange={onChange}
            disabled={uploading || busy}
            data-testid="onboard-byo-fileinput"
            className="text-sm text-black"
          />
          {uploading && (
            <p
              data-testid="onboard-byo-uploading"
              className="text-xs italic text-gray-800"
            >
              Uploading…
            </p>
          )}
        </div>
      )}

      {uploadError && (
        <div
          data-testid="onboard-byo-error"
          className="border-2 border-[#a00] bg-[#fee] p-3 text-sm text-black"
        >
          <p className="mb-1 font-bold uppercase tracking-wide text-[#700]">
            Upload error
          </p>
          <pre className="whitespace-pre-wrap break-words font-mono text-[11px] text-black">
            {uploadError}
          </pre>
        </div>
      )}

      <div className="flex gap-2 justify-end">
        {hasUpload && (
          // Skip only makes sense when the user has uploaded something —
          // it discards the upload and falls back to template-only.
          // Without an upload, "Skip" and "Continue" would do the exact
          // same thing (both proceed with template-only content), so we
          // hide Skip in that case to avoid the duplicate-button absurdity.
          <button
            type="button"
            data-testid="onboard-byo-skip"
            onClick={onSkip}
            disabled={uploading || busy}
            className="border border-[#404040] bg-[#d0d0d0] px-4 py-1 text-sm text-black cursor-pointer hover:bg-[#e0e0e0] disabled:opacity-50"
          >
            Discard upload — use template only
          </button>
        )}
        <button
          type="button"
          data-testid="onboard-byo-continue"
          onClick={onContinue}
          disabled={uploading || busy}
          className="self-start border border-[#404040] bg-[#d0d0d0] px-4 py-1 text-sm font-bold text-black cursor-pointer hover:bg-[#e0e0e0] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {hasUpload ? 'Continue — provision with imported content' : 'Continue — provision with template only'}
        </button>
      </div>
    </section>
  );
}
